/**
 * @poc-to-production
 *
 * QuarantinedRulesPanel
 *
 * Renders the rules the Family-1 validator rejected (ADR 0007). The
 * runner / live engine refuses to evaluate these rules against
 * elements, so they never appear as violations — but a reviewer needs
 * to see them. This panel is the demo's "human-in-the-loop" surface:
 * it shows WHY each rule is parked, what the validator flagged, and
 * (in production) would carry a "Review" button that routes to the
 * editor flow.
 *
 * Read-only today. The "Review" button is a no-op placeholder so the
 * pitch can talk through the workflow.
 */
import { useEffect, useState } from "preact/hooks";
import { useEngineStore } from "../stores/engineStore";
import { useUiModeStore } from "../stores/uiModeStore";
import { Card } from "./Card";

export function QuarantinedRulesPanel() {
  const rules = useEngineStore(s => s.rules);
  const loaded = useEngineStore(s => s.loaded);
  const uiMode = useUiModeStore(s => s.mode);
  const quarantined = rules.filter(r => r.confidence?.tier === "rejected");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [previewRuleId, setPreviewRuleId] = useState<string | null>(null);

  if (uiMode !== "technical") return null;
  if (!loaded || quarantined.length === 0) return null;

  return (
    <Card
      title="Quarantined rules"
      defaultOpen={false}
      tone="warning"
      accent="var(--vera-violation)"
      meta={`${quarantined.length} needs review`}
    >
      <div>
        {quarantined.map(rule => {
          const isOpen = expanded === rule.ruleId;
          const findings = rule.confidence?.findings ?? [];
          const errorCount = findings.filter(
            f => f.severity === "error"
          ).length;
          return (
            <div
              key={rule.ruleId}
              style={{
                padding: "6px 8px",
                marginBottom: 4,
                borderRadius: 3,
                background: "rgba(255,255,255,0.04)",
                borderLeft: "2px solid var(--vera-violation)",
              }}
            >
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : rule.ruleId)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  color: "inherit",
                  font: "inherit",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <span
                    style={{ fontWeight: 600, color: "var(--vera-violation)" }}
                  >
                    §{rule.source.section}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      color: "var(--vera-text-dim)",
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {errorCount} error{errorCount === 1 ? "" : "s"} ·{" "}
                    {rule.source.jurisdictionId}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--vera-text-dim)",
                    marginTop: 2,
                  }}
                >
                  {rule.source.title}
                </div>
              </button>
              {isOpen && (
                <div style={{ marginTop: 6 }}>
                  <ul
                    style={{
                      margin: "0 0 6px",
                      paddingLeft: 18,
                      fontSize: 11,
                      color: "var(--vera-text-dim)",
                      lineHeight: 1.4,
                    }}
                  >
                    {findings
                      .filter(
                        f => f.severity === "error" || f.severity === "warning"
                      )
                      .map((f, i) => (
                        <li key={`${f.check}-${i}`}>
                          <code style={{ fontSize: 10 }}>{f.check}</code>:{" "}
                          {f.message}
                        </li>
                      ))}
                  </ul>
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setPreviewRuleId(rule.ruleId);
                    }}
                    style={{
                      fontSize: 10,
                      padding: "3px 8px",
                      background: "rgba(255,255,255,0.06)",
                      color: "var(--vera-text)",
                      border: "1px solid var(--vera-border)",
                      borderRadius: 2,
                      cursor: "pointer",
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                    title="Preview the production review workflow"
                  >
                    Review
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {previewRuleId && (
        <ReviewWorkflowPreview
          ruleId={previewRuleId}
          rule={quarantined.find(r => r.ruleId === previewRuleId) ?? null}
          onClose={() => setPreviewRuleId(null)}
        />
      )}
    </Card>
  );
}

/**
 * Modal that explains what clicking "Review" would do in production.
 * Kept deliberately simple — no editor, no form, just a description of
 * the workflow so the pitch can talk through it without us having to
 * build the editor today.
 */
function ReviewWorkflowPreview({
  ruleId,
  rule,
  onClose,
}: {
  ruleId: string;
  rule: {
    source: { code: string; section: string; title: string };
    confidence?: { findings: Array<{ severity: string; message: string }> };
  } | null;
  onClose: () => void;
}) {
  const findingCount = rule?.confidence?.findings.length ?? 0;

  // Escape closes the dialog (keyboard equivalent of the backdrop
  // click). We attach to window because the dialog may not have focus
  // when the user presses Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-preview-title"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      {/* Backdrop is a real button so screen readers and keyboard
          users get a labelled "Close" action without us tripping the
          a11y lint rules with a clickable bare div. Visually it's
          just a translucent black overlay. */}
      <button
        type="button"
        aria-label="Close review preview"
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      />
      <div
        style={{
          position: "relative",
          width: "min(540px, 90vw)",
          maxHeight: "80vh",
          overflowY: "auto",
          background: "var(--vera-panel-bg)",
          border: "1px solid var(--vera-border)",
          borderRadius: 6,
          padding: 20,
          color: "var(--vera-text)",
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "var(--vera-text-dim)",
            marginBottom: 6,
          }}
        >
          Reviewer workflow — preview
        </div>
        <h2
          id="review-preview-title"
          style={{ margin: "0 0 12px", fontSize: 16 }}
        >
          {rule
            ? `${rule.source.code} §${rule.source.section} — ${rule.source.title}`
            : ruleId}
        </h2>
        <p style={{ margin: "0 0 10px" }}>
          In production, clicking <strong>Review</strong> would open a
          side-by-side editor showing three things at once:
        </p>
        <ol style={{ margin: "0 0 12px 20px", padding: 0 }}>
          <li>
            The rule's structured JSON (the machine-readable form the engine
            evaluates).
          </li>
          <li>
            The original code text from the publisher's PDF, with the relevant
            clauses highlighted.
          </li>
          <li>
            The {findingCount} validator finding{findingCount === 1 ? "" : "s"}{" "}
            that put this rule into quarantine, so the reviewer knows what
            specifically to look at.
          </li>
        </ol>
        <p style={{ margin: "0 0 10px" }}>
          The reviewer — a code consultant, an architect, or an Autodesk
          ontology editor — can then take one of three actions:
        </p>
        <ul style={{ margin: "0 0 12px 20px", padding: 0 }}>
          <li>
            <strong>Fix the rule JSON.</strong> Fill in the missing
            <code> appliesIf </code>condition, correct a misnamed property,
            tighten an exception's verbatim text.
          </li>
          <li>
            <strong>Extend the ontology.</strong> Add the missing class or
            property to <code>vera-core.ttl</code> so the rule's vocabulary
            becomes recognised system-wide.
          </li>
          <li>
            <strong>Mark out of scope.</strong> Some rules genuinely cannot be
            evaluated from BIM data alone (door opening force, for example). The
            reviewer flags these so they don't keep failing validation, and Vera
            surfaces them as "not automatable" in the panel.
          </li>
        </ul>
        <p style={{ margin: "0 0 16px", color: "var(--vera-text-dim)" }}>
          After the reviewer submits, the rule re-runs through the validator and
          either clears to <em>medium</em> / <em>high</em> or stays quarantined
          with new findings to address. The full editor isn't built yet; this
          dialog stands in for the workflow on demo day.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "6px 14px",
              background: "rgba(127,179,213,0.18)",
              color: "#7fb3d5",
              border: "1px solid var(--vera-border)",
              borderRadius: 3,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
